import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  fetchItems,
  loadMoreItems,
  setFilters as setReduxFilters,
  clearFilters as clearReduxFilters,
} from '../../store/slices/itemsSlice';
import ItemFavoriteButton from '../../components/items/ItemFavoriteButton';
import { FAVORITE_KIND } from '../../utils/favoriteKeys';
import SEO from 'components/seo/SEO';
import SeoPageIntro from '../../components/seo/SeoPageIntro';
import { getCategorySeoBySlug, getCategorySeoByValue } from '../../constants/seoConfig';
import { buildBreadcrumbSchema } from '../../utils/seoStructuredData';
import styled from 'styled-components';
import { getItemPublicPath } from '../../utils/itemLinks';
import { trackItemClicked } from '../../utils/analytics';
import { analyticsAPI } from '../../api/itemEngagementAPI';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import { addRecentlyViewed } from '../../utils/recentlyViewed';
import {
  getSavedFilters,
  saveFilter,
  deleteSavedFilter,
  isFilterMeaningful,
} from '../../utils/savedFilters';
import { formatListingPriceLabel } from '../../utils/formatPrice';
import { getApproximateViewerCoordinates } from '../../utils/viewerGeo';
import { ItemSkeletonGrid } from '../../components/items/ItemSkeleton';
import RecentlyViewedStrip from '../../components/items/RecentlyViewedStrip';
import EmptyItemsState from '../../components/items/EmptyItemsState';
import FeaturedStrip from '../../components/items/FeaturedStrip';
import CategoryCarousel from '../../components/items/CategoryCarousel';
import {
  PRE_OWNED_BROWSE_CAROUSEL_CATEGORIES,
  PRE_OWNED_BROWSE_FILTER_CATEGORIES,
} from '../../constants/preOwnedBrowseCategories';
import {
  HOBBIES_FINDS_BROWSE_CATEGORIES,
  getHobbiesFindsBrowseApiCategories,
  isHobbiesFindsCategory,
} from '../../constants/hobbiesFindsCategories';
import {
  getFashionCuratedBrowseApiCategories,
  isFashionCuratedCategory,
} from '../../constants/fashionCuratedCategories';
import { ItemCornerBadges, ItemTrustStrip } from '../../components/items/ItemCardBadges';
import { resolveListingBrand } from '../../components/items/ListingDetailShared';
import { useTranslation } from 'react-i18next';
import {
  SHOP_DEPARTMENTS,
  FILTER_CONDITIONS,
  FILTER_COLOR_GROUPS,
  FILTER_MATERIALS,
  FILTER_LENGTH_GROUPS,
  BOOK_TYPES,
  BOOK_LANGUAGES,
  BOOK_AUDIENCES,
  PET_TYPES,
  FILTER_LABELS,
  EMPTY_BROWSE_FILTERS,
  getSubcategoriesForCategory,
  getBrowseSizeOptions,
  isClothingCategory,
  parseMultiFilterValue,
  toggleMultiFilterValue,
} from '../../constants/itemBrowseFilters';
import {
  KITCHEN_DINING_GROUPS,
  KITCHEN_DINING_ROOT,
  getKitchenDiningTypes,
  isHomeKitchenListingCategory,
  isKitchenDiningSubcategory,
  joinKitchenDiningSubcategoryPath,
  parseKitchenDiningSubcategoryPath,
} from '../../constants/kitchenDiningCategorySchema';
import BrandSelector from '../../components/items/BrandSelector';

/* ============================================================
 * Layout primitives
 * ============================================================ */

const Page = styled.div`
  background: ${({ $beige }) => ($beige ? '#f6f1e8' : '#a8e6cf')};
  min-height: calc(100vh - 48px);
`;

const PageInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.5rem 1rem 4rem;
`;

const DefectedBanner = styled.div`
  margin: 0 auto 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fbbf24;
  border-radius: 0.75rem;
  color: #78350f;
`;

const DefectedBannerTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const DefectedBannerBody = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
`;

/* ============================================================
 * Unified Toolbar  (#4 search, #5 unified, #14 chips own row,
 * #16 distance sort, #20 grid/list toggle)
 * ============================================================ */

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1rem;
  align-items: center;
  margin-top: 0.1rem;
  margin-bottom: 0.4rem;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0.15rem 0;
`;

const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0;
  background: transparent;
  color: var(--text-primary);
  border: none;
  border-bottom: 1px solid transparent;
  border-radius: 0;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  min-height: 40px;

  &:hover { color: var(--primary-color); border-bottom-color: var(--primary-color); }
  &:active { opacity: 0.85; }

  ${(p) =>
    p.$primary &&
    `
    color: var(--primary-color);
    border-bottom-color: var(--primary-color);
    &:hover { color: var(--primary-color); filter: brightness(0.95); }
  `}
`;

const ToolbarBadge = styled.span`
  background: transparent;
  color: var(--primary-color);
  padding: 0;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.25rem;
`;

const SortSelect = styled.select`
  padding: 0.35rem 0;
  border: none;
  border-bottom: 1px solid var(--border-color);
  border-radius: 0;
  background-color: transparent;
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  min-height: 40px;
`;

const ViewToggle = styled.div`
  display: inline-flex;
  gap: 0.75rem;
  border: none;
  border-radius: 0;
  overflow: visible;
`;

const ViewToggleButton = styled.button`
  padding: 0.35rem 0;
  background: transparent;
  color: ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--text-secondary)')};
  border: none;
  border-bottom: 1px solid ${(p) => (p.$active ? 'var(--primary-color)' : 'transparent')};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  min-height: 40px;
  min-width: 36px;
`;

const ResultCount = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-left: auto;
  white-space: nowrap;
`;

/* ============================================================
 * Active filter chips own row  (#14)
 * ============================================================ */

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const ActiveFilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: white;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary);
  border: 1.5px solid var(--border-color);
`;

const ChipValue = styled.span`
  color: var(--primary-color);
`;

const RemoveChipButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  line-height: 1;
  &:hover { color: #e74c3c; }
`;

const ClearAllLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  padding: 0.25rem 0.5rem;

  &:hover { filter: brightness(0.85); }
`;

/* ============================================================
 * Saved filters dropdown  (#18 — localStorage version)
 * ============================================================ */

const SavedWrap = styled.div`
  position: relative;
`;

const SavedMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  background: white;
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 240px;
  z-index: 30;
  padding: 0.4rem;
`;

const SavedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.5rem;
  border-radius: 0.35rem;
  cursor: pointer;

  &:hover { background: rgba(102, 126, 234, 0.08); }
`;

const SavedName = styled.span`
  flex: 1;
  font-size: 0.9rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const SavedDelete = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.85rem;
  &:hover { color: #e74c3c; }
`;

/* ============================================================
 * Items grid + cards  (#7 aspect-ratio, #8 price hierarchy,
 * #9 badges, #21 trust strip)
 * ============================================================ */

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 1rem;
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ItemCardWrap = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  background: transparent;
  overflow: hidden;
  border: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.92;
  }
`;

const ImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
    transition: transform 0.3s ease;
  }
  ${ItemCardWrap}:hover & img { transform: scale(1.15); }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: var(--text-light);
  background-color: var(--bg-secondary);
`;

const ImageCountBadge = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  background-color: rgba(0, 0, 0, 0.65);
  color: white;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
`;

const Body = styled.div`
  padding: 0.55rem 0.15rem 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Price = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.01em;
  line-height: 1.2;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: var(--text-light);
  font-weight: 500;
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

/* List view variant */

const ListCard = styled(Link)`
  display: flex;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  background: transparent;
  border: none;
  overflow: hidden;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.92; }
`;

const ListImageWrap = styled(ImageWrap)`
  width: 140px;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  background-color: var(--bg-secondary);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 600px) {
    width: 100px;
  }
`;

const ListBody = styled.div`
  flex: 1;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

/* ============================================================
 * Filter modal — bottom sheet on mobile  (#13)
 * ============================================================ */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.55);
  display: ${(p) => (p.isOpen ? 'flex' : 'none')};
  z-index: 1000;
  padding: 1rem;

  /* Desktop: centered modal */
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    /* Mobile: anchor to bottom (bottom sheet) */
    padding: 0;
    align-items: flex-end;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  max-width: 720px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;

  @media (max-width: 768px) {
    border-radius: 1rem 1rem 0 0;
    max-height: 88vh;
    animation: slideUp 0.25s ease;
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const SheetGrabber = styled.div`
  width: 40px;
  height: 4px;
  background: var(--border-color);
  border-radius: 4px;
  margin: 0.5rem auto 0;
  display: none;

  @media (max-width: 768px) { display: block; }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.75rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  &:hover { background-color: #f0f0f0; color: var(--text-primary); }
`;

const ModalBody = styled.div`
  padding: 1.25rem 1.5rem 5.5rem;
`;

const FilterSectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
`;

const FilterGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const FilterLabel = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.4rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FilterInput = styled.input`
  padding: 0.7rem 0.85rem;
  border: 1.5px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 0.95rem;
  width: 100%;
  background: white;
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(102,126,234,0.18); }
`;

const FilterSelect = styled.select`
  padding: 0.7rem 0.85rem;
  border: 1.5px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 0.95rem;
  width: 100%;
  background: white;
  cursor: pointer;
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const ConditionChip = styled.button`
  padding: 0.45rem 0.9rem;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  border-radius: 999px;
  background: ${(p) => (p.$active ? 'var(--primary-color)' : 'white')};
  color: ${(p) => (p.$active ? 'white' : 'var(--text-primary)')};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: var(--primary-color); }
`;

const ModalFooter = styled.div`
  position: sticky;
  bottom: 0;
  background: white;
  padding: 0.75rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 0.75rem;
`;

const FooterButton = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  border: none;

  &.primary { background: var(--primary-color); color: white; }
  &.secondary { background: white; color: var(--text-secondary); border: 1.5px solid var(--border-color); }
`;

/* ============================================================
 * Inline "Offer Your Item" CTA — sits in the toolbar next to
 * Filters / View / Saved.
 * ============================================================ */

const OfferLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.95rem;
  background: var(--primary-color);
  color: white;
  border: 1.5px solid var(--primary-color);
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  cursor: pointer;
  min-height: 44px;
  transition: filter 0.15s ease, transform 0.1s ease;
  white-space: nowrap;

  &:hover { filter: brightness(1.05); color: white; }
  &:active { transform: translateY(1px); }

  @media (max-width: 480px) {
    span.label-long { display: none; }
  }
`;

/* ============================================================
 * Sentinel for infinite scroll
 * ============================================================ */

const Sentinel = styled.div`
  height: 1px;
`;

const LoadingMoreLine = styled.div`
  text-align: center;
  padding: 1.25rem 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

/* ============================================================
 * Component
 * ============================================================ */

const FILTER_CATEGORIES = PRE_OWNED_BROWSE_FILTER_CATEGORIES;

const formatActiveValue = (key, value) => {
  if (key === 'minPrice' || key === 'maxPrice') return `${value} EGP`;
  if (key === 'condition') return parseMultiFilterValue(value).join(', ');
  return value;
};

const resolveImageUrl = (raw) => {
  if (!raw) return null;
  if (raw.startsWith('http')) return raw;
  const isDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');
  const base = isDev
    ? 'http://127.0.0.1:8080'
    : '';
  return `${base}${raw}`;
};

const pickImageUrl = (item) => {
  if (item.primary_image) return resolveImageUrl(item.primary_image);
  if (item.image_url) return resolveImageUrl(item.image_url);
  if (item.images && item.images.length > 0) return resolveImageUrl(item.images[0].image);
  return null;
};

const ItemsPage = ({ defectedOnly = false, hobbiesOnly = false } = {}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { categorySlug } = useParams();
  const { items, loading, loadingMore, error, pagination } = useSelector((state) => state.items);
  const carouselCategories = hobbiesOnly
    ? HOBBIES_FINDS_BROWSE_CATEGORIES
    : PRE_OWNED_BROWSE_CAROUSEL_CATEGORIES;
  const filterCategories = hobbiesOnly
    ? HOBBIES_FINDS_BROWSE_CATEGORIES
    : FILTER_CATEGORIES;

  const { filters: urlFilters, setFilters: setUrlFilters, clearFilters: clearUrlFilters } = useUrlFilters();

  const categorySeo =
    getCategorySeoBySlug(categorySlug) || getCategorySeoByValue(urlFilters.category);

  useEffect(() => {
    const fromRoute = getCategorySeoBySlug(categorySlug);
    if (fromRoute && urlFilters.category !== fromRoute.categoryValue) {
      setUrlFilters({ ...urlFilters, category: fromRoute.categoryValue });
    }
  }, [categorySlug]); // eslint-disable-line react-hooks/exhaustive-deps -- sync route slug → filter once

  // The toolbar reads/writes URL state. Local "draft" filter state for the
  // modal lives separately so users can change values without firing a
  // refetch on every keystroke.
  const [modalDraft, setModalDraft] = useState(urlFilters);
  useEffect(() => setModalDraft(urlFilters), [urlFilters]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [view, setView] = useState(urlFilters.view === 'list' ? 'list' : 'grid');
  useEffect(() => setView(urlFilters.view === 'list' ? 'list' : 'grid'), [urlFilters.view]);

  const [page, setPage] = useState(1);
  const [savedOpen, setSavedOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  useEffect(() => setSaved(getSavedFilters()), []);

  /** IP-derived approximate coords for ranking (optional); quality-first then nearer sellers. */
  const [viewerCoords, setViewerCoords] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getApproximateViewerCoordinates().then((c) => {
      if (!cancelled && c?.lat != null && c?.lon != null) setViewerCoords(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ----- impressions ----- */

  const impressionsSent = useRef(new Set());
  const observerRef = useRef(null);
  const itemCardRef = useCallback((node) => {
    if (!node) return;
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = Number(entry.target.dataset.itemId);
              if (id && !impressionsSent.current.has(id)) {
                impressionsSent.current.add(id);
                analyticsAPI.recordImpression(id, 'item');
              }
            }
          });
        },
        { threshold: 0.5 },
      );
    }
    observerRef.current.observe(node);
  }, []);
  useEffect(
    () => () => {
      analyticsAPI.flushImpressions();
      observerRef.current?.disconnect();
    },
    [],
  );

  /* ----- fetch on filter/page reset ----- */

  // Build the params we send to the backend.
  const fetchParams = useMemo(() => {
    const params = { ...urlFilters };
    delete params.view; // UI-only, never goes to backend
    const sortVal = params.sort;
    delete params.sort;
    Object.keys(params).forEach((key) => {
      if (params[key] === '' || params[key] == null) delete params[key];
    });
    if (params.subcategory && params.category === 'Books') {
      params.book_subcategory = params.subcategory;
      delete params.subcategory;
    }
    if (defectedOnly) params.is_defected = 'true';
    if (hobbiesOnly) {
      delete params.department;
      if (!params.category) {
        params.categories = getHobbiesFindsBrowseApiCategories();
      }
    } else if (!defectedOnly && !params.category) {
      params.categories = getFashionCuratedBrowseApiCategories();
      if (!params.page_size) {
        params.page_size = '24';
      }
    }
    if (viewerCoords?.lat != null && viewerCoords?.lon != null) {
      params.user_lat = String(viewerCoords.lat);
      params.user_lon = String(viewerCoords.lon);
    }
    if (sortVal === 'nearest') params.browse_rank = 'nearest';
    else if (sortVal) params.sort = sortVal;
    return params;
  }, [urlFilters, defectedOnly, hobbiesOnly, viewerCoords]);

  // Reset page + refetch when fetchParams change.
  useEffect(() => {
    setPage(1);
    dispatch(fetchItems({ ...fetchParams, page: 1 }));
    // Mirror to legacy redux filter slot so any other consumer (e.g. mobile
    // shared store, or the SearchBar component) still sees the same value.
    dispatch(setReduxFilters(urlFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchParams]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  /* ----- toolbar handlers ----- */

  const setFilter = (patch) => setUrlFilters((cur) => ({ ...cur, ...patch }));

  const handleCategoryClick = (categoryValue) => {
    const next = {
      ...urlFilters,
      category: categoryValue,
      subcategory: '',
      book_subcategory: '',
      book_type: '',
      book_language: '',
      book_audience: '',
      pet_type: '',
      size: '',
      length: '',
    };
    if (categoryValue !== 'Books') {
      next.book_subcategory = '';
      next.book_type = '';
      next.book_language = '';
      next.book_audience = '';
    }
    setUrlFilters(next);
  };

  const removeFilter = (key) => setFilter({ [key]: '' });

  /* ----- modal handlers ----- */

  const applyModal = () => {
    setUrlFilters(modalDraft);
    setIsFilterOpen(false);
  };

  const kitchenFilter = parseKitchenDiningSubcategoryPath(
    isHomeKitchenListingCategory(modalDraft.category) ? modalDraft.subcategory : '',
  );

  const setKitchenFilter = (patch) => {
    const next = { ...kitchenFilter, ...patch };
    if (patch.group !== undefined) next.type = '';
    setModalDraft((d) => ({
      ...d,
      subcategory: joinKitchenDiningSubcategoryPath([
        KITCHEN_DINING_ROOT,
        next.group,
        next.type,
      ]),
    }));
  };

  const handleEscapeKey = useCallback(
    (e) => {
      if (e.key === 'Escape' && isFilterOpen) setIsFilterOpen(false);
    },
    [isFilterOpen],
  );
  useEffect(() => {
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [handleEscapeKey]);

  /* ----- sort + paginated items ----- */

  // For non-distance sorts, do client-side reordering on top of the
  // (already-ranked) page. With pagination this only reorders the
  // currently-loaded slice, which matches user expectations for marketplace
  // browse pages.
  const sortedItems = useMemo(() => {
    const source = hobbiesOnly
      ? items.filter((item) => isHobbiesFindsCategory(item.category))
      : defectedOnly
        ? items
        : items.filter((item) => isFashionCuratedCategory(item.category));
    const byPriceLow = (a, b) => parseFloat(a.price) - parseFloat(b.price);
    const byPriceHigh = (a, b) => parseFloat(b.price) - parseFloat(a.price);
    const byNewest = (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    const byOldest = (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    switch (urlFilters.sort) {
      case 'price-low': return [...source].sort(byPriceLow);
      case 'price-high': return [...source].sort(byPriceHigh);
      case 'newest': return [...source].sort(byNewest);
      case 'oldest': return [...source].sort(byOldest);
      case 'nearest':
        return source;
      // '' / recommended → server: quality score then proximity when coords sent
      default: return source;
    }
  }, [items, urlFilters.sort, hobbiesOnly, defectedOnly]);

  /* ----- infinite scroll ----- */

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!pagination.has_next || loading || loadingMore) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e.isIntersecting) return;
        const next = page + 1;
        setPage(next);
        dispatch(loadMoreItems({ ...fetchParams, page: next }));
      },
      { rootMargin: '300px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [dispatch, page, pagination.has_next, loading, loadingMore, fetchParams]);

  /* ----- saved filters ----- */

  const handleSaveFilter = () => {
    if (!isFilterMeaningful(urlFilters)) {
      toast.info('Set some filters first, then save them.');
      return;
    }
    const name = window.prompt('Name this saved search:');
    if (!name) return;
    saveFilter(name, urlFilters);
    setSaved(getSavedFilters());
    toast.success(`Saved "${name}"`);
  };

  /* ----- Active chip list ----- */
  const activeChips = useMemo(() => {
    const out = [];
    Object.entries(urlFilters).forEach(([key, value]) => {
      if (!value || key === 'view' || key === 'sort' || key === 'category') return;
      out.push({ key, label: FILTER_LABELS[key] || key, value: formatActiveValue(key, value) });
    });
    return out;
  }, [urlFilters]);

  /* ----- card renderer (used by both grid and list) ----- */

  const renderCard = (item) => {
    const imageUrl = pickImageUrl(item);
    const hasMultipleImages = item.images && item.images.length > 1;
    const onCardClick = () => {
      addRecentlyViewed(item);
      trackItemClicked(item.id, { item_type: 'item', category: item.category });
      analyticsAPI.recordClick(item.id, 'item');
    };

    return (
      <ItemCardWrap
        key={item.id}
        to={getItemPublicPath(item)}
        state={{ itemId: item.id }}
        onClick={onCardClick}
        ref={itemCardRef}
        data-item-id={item.id}
      >
        <ImageWrap>
          <ItemFavoriteButton
            itemId={item.id}
            favoriteKind={FAVORITE_KIND.ITEM}
            category={item.category}
          />
          <ItemCornerBadges item={item} />
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={item.title}
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <ImagePlaceholder>📦</ImagePlaceholder>
          )}
          {hasMultipleImages && <ImageCountBadge>📷 {item.images.length}</ImageCountBadge>}
        </ImageWrap>
        <Body>
          <Price>{formatListingPriceLabel(item)}</Price>
          <Meta>
            <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
            {item.condition ? <span>· {item.condition}</span> : null}
          </Meta>
          <ItemTrustStrip item={item} />
        </Body>
      </ItemCardWrap>
    );
  };

  const renderListCard = (item) => {
    const imageUrl = pickImageUrl(item);
    const onCardClick = () => {
      addRecentlyViewed(item);
      trackItemClicked(item.id, { item_type: 'item', category: item.category });
      analyticsAPI.recordClick(item.id, 'item');
    };
    return (
      <ListCard
        key={item.id}
        to={getItemPublicPath(item)}
        state={{ itemId: item.id }}
        onClick={onCardClick}
        ref={itemCardRef}
        data-item-id={item.id}
      >
        <ListImageWrap>
          <ItemCornerBadges item={item} />
          {imageUrl ? <img src={imageUrl} alt={item.title} loading="lazy" decoding="async" /> : <ImagePlaceholder>📦</ImagePlaceholder>}
        </ListImageWrap>
        <ListBody>
          <Price>{formatListingPriceLabel(item)}</Price>
          <Meta>
            <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
            {item.condition ? <span>· {item.condition}</span> : null}
          </Meta>
          <ItemTrustStrip item={item} />
        </ListBody>
      </ListCard>
    );
  };

  const browseSeo = defectedOnly
    ? {
        title: 'Defected Items — Flea Marketplace Egypt',
        description:
          'Browse defected items listed for recycling, parts, or reuse. Help reduce waste by giving broken and partly-used items a second life.',
        keywords: 'defected items Egypt, recycling marketplace, reuse parts Cairo',
        url: '/defected-items',
        h1: 'Defected Items — Recycling & Reuse',
      }
    : hobbiesOnly
      ? {
          title: 'Hobbies Finds — Flea Marketplace Egypt',
          description:
            'Browse toys, games, arts & crafts, sports, books, stationary, and musical instruments on Flea Marketplace Egypt.',
          keywords:
            'hobbies Egypt, toys Egypt, sports equipment Egypt, books Egypt, musical instruments Egypt, crafts Egypt',
          url: '/hobbies-finds',
          h1: 'Hobbies Finds',
        }
      : categorySeo
        ? {
            title: categorySeo.title,
            description: categorySeo.description,
            keywords: categorySeo.keywords,
            url: categorySeo.path,
            h1: categorySeo.h1,
          }
        : {
            title: 'Pre-Owned Items - Flea Marketplace Egypt',
            description:
              'Buy and sell second-hand items in Egypt on Flea Marketplace.',
            keywords:
              'browse items, used items Egypt, secondhand items, flea market items, buy used items, sell used items',
            url: '/pre-owned-items',
            h1: 'Pre-Owned Items Marketplace in Egypt',
          };

  const browseBreadcrumb = buildBreadcrumbSchema(
    defectedOnly
      ? [
          { name: 'Home', url: '/' },
          { name: 'Defected Items', url: '/defected-items' },
        ]
      : hobbiesOnly
        ? [
            { name: 'Home', url: '/' },
            { name: 'Hobbies Finds', url: '/hobbies-finds' },
          ]
        : categorySeo
          ? [
              { name: 'Home', url: '/' },
              { name: 'Browse', url: '/pre-owned-items' },
              { name: categorySeo.name, url: categorySeo.path },
            ]
          : [
              { name: 'Home', url: '/' },
              { name: 'Browse Items', url: '/pre-owned-items' },
            ],
  );

  /* ============================================================ */
  return (
    <Page $beige={!defectedOnly}>
      <PageInner>
      <SEO
        title={browseSeo.title}
        description={browseSeo.description}
        keywords={browseSeo.keywords}
        url={browseSeo.url}
        jsonLd={browseBreadcrumb}
      />
      <SeoPageIntro
        h1={defectedOnly || hobbiesOnly ? browseSeo.h1 : undefined}
        description={undefined}
      />
      {/* Categories — first on page */}
      <CategoryCarousel
        categories={carouselCategories}
        selectedCategory={urlFilters.category || ''}
        onSelectCategory={handleCategoryClick}
        toggleOnReselect
      />

      {defectedOnly && (
        <DefectedBanner role="note" aria-label="Defected items info">
          <DefectedBannerTitle>{t('browse.defectedTitle')}</DefectedBannerTitle>
          <DefectedBannerBody>{t('browse.defectedBody')}</DefectedBannerBody>
        </DefectedBanner>
      )}

      {/* Admin-curated featured strip — auto-hides when empty */}
      <FeaturedStrip
        scope={defectedOnly ? 'defected_items' : 'regular_items'}
        department={hobbiesOnly ? '' : (urlFilters.department || '')}
        sectionTitle={
          hobbiesOnly
            ? t('browse.featuredHobbies')
            : defectedOnly
              ? t('browse.featuredDefected')
              : t('browse.featuredItems')
        }
        includeCategory={
          hobbiesOnly
            ? isHobbiesFindsCategory
            : defectedOnly
              ? undefined
              : isFashionCuratedCategory
        }
      />

      {/* Recently viewed (#17) */}
      <RecentlyViewedStrip
        scope={defectedOnly ? 'defected_items' : 'regular_items'}
        includeCategory={
          hobbiesOnly
            ? isHobbiesFindsCategory
            : defectedOnly
              ? undefined
              : isFashionCuratedCategory
        }
      />

      {/* Toolbar (#5 unified, #20 grid/list) — search lives in the global
          header bar above so we don't render a duplicate here. */}
      <Toolbar>
        <ToolbarButton type="button" onClick={() => setIsFilterOpen(true)} aria-label={t('common.filters')}>
          ⚙️ {t('common.filters')}
          {activeChips.length > 0 && <ToolbarBadge>{activeChips.length}</ToolbarBadge>}
        </ToolbarButton>

        <SortSelect
          aria-label="Sort items"
          value={urlFilters.sort || ''}
          onChange={(e) => setFilter({ sort: e.target.value })}
        >
          <option value="">{t('browse.sortRecommended')}</option>
          <option value="nearest">{t('browse.sortNearest')}</option>
          <option value="newest">{t('common.newest')}</option>
          <option value="oldest">{t('common.oldest')}</option>
          <option value="price-low">{t('common.priceLow')}</option>
          <option value="price-high">{t('common.priceHigh')}</option>
        </SortSelect>

        <ViewToggle role="group" aria-label="View mode">
          <ViewToggleButton type="button" $active={view === 'grid'} onClick={() => setFilter({ view: '' })} aria-label="Grid view">
            ▦
          </ViewToggleButton>
          <ViewToggleButton type="button" $active={view === 'list'} onClick={() => setFilter({ view: 'list' })} aria-label="List view">
            ☰
          </ViewToggleButton>
        </ViewToggle>

        <OfferLink
          to={
            defectedOnly
              ? '/create-item?defected=1'
              : hobbiesOnly
                ? '/create-item?group=hobbies'
                : '/create-item'
          }
          aria-label="Offer your item"
        >
          <span aria-hidden>＋</span>
          <span className="label-long">{t('browse.offerYourItem')}</span>
        </OfferLink>

        <SavedWrap>
          <ToolbarButton type="button" onClick={() => setSavedOpen((o) => !o)} aria-haspopup="menu" aria-expanded={savedOpen}>
            ⭐ {t('browse.saved')}
          </ToolbarButton>
          {savedOpen && (
            <SavedMenu role="menu" onMouseLeave={() => setSavedOpen(false)}>
              <SavedRow onClick={() => { handleSaveFilter(); setSavedOpen(false); }}>
                <SavedName>＋ {t('browse.saveCurrentFilters')}</SavedName>
              </SavedRow>
              {saved.length === 0 ? (
                <div style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {t('browse.noSavedSearches')}
                </div>
              ) : (
                saved.map((s) => (
                  <SavedRow key={s.id} onClick={() => { setUrlFilters(s.filters); setSavedOpen(false); }}>
                    <SavedName>{s.name}</SavedName>
                    <SavedDelete
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteSavedFilter(s.id); setSaved(getSavedFilters()); }}
                      aria-label={`Delete ${s.name}`}
                    >
                      ✕
                    </SavedDelete>
                  </SavedRow>
                ))
              )}
            </SavedMenu>
          )}
        </SavedWrap>

        <ResultCount>
          {pagination.count > 0 ? (
            <>{t('browse.showingCount', { count: sortedItems.length, total: pagination.count })}</>
          ) : null}
        </ResultCount>
      </Toolbar>

      {/* Active chips own row (#14) */}
      {activeChips.length > 0 && (
        <ChipsRow>
          {activeChips.map((c) => (
            <ActiveFilterChip key={c.key}>
              <span>{c.label}:</span>
              <ChipValue>{c.value}</ChipValue>
              <RemoveChipButton onClick={() => removeFilter(c.key)} aria-label={`Remove ${c.label}`}>
                ×
              </RemoveChipButton>
            </ActiveFilterChip>
          ))}
          <ClearAllLink type="button" onClick={() => clearUrlFilters()}>
            {t('common.clearAll')}
          </ClearAllLink>
        </ChipsRow>
      )}

      {/* Items grid (#3 skeletons, #15 empty state, #20 list view) */}
      {loading && items.length === 0 ? (
        <ItemSkeletonGrid count={8} />
      ) : sortedItems.length === 0 ? (
        <EmptyItemsState
          filters={urlFilters}
          onRemoveFilter={removeFilter}
          onClearAll={() => clearUrlFilters()}
        />
      ) : view === 'list' ? (
        <ItemsList>{sortedItems.map(renderListCard)}</ItemsList>
      ) : (
        <ItemsGrid>{sortedItems.map(renderCard)}</ItemsGrid>
      )}

      {/* Infinite-scroll sentinel + status line (#2) */}
      {pagination.has_next && <Sentinel ref={sentinelRef} />}
      {loadingMore && <LoadingMoreLine>{t('browse.loadingMore')}</LoadingMoreLine>}
      {!loading && !loadingMore && !pagination.has_next && pagination.count > 0 && (
        <LoadingMoreLine>
          {pagination.count === 1
            ? t('browse.endOfListOne')
            : t('browse.endOfList', { count: pagination.count })}
        </LoadingMoreLine>
      )}

      {/* Filter modal — bottom sheet on mobile (#13) */}
      <ModalOverlay isOpen={isFilterOpen} onClick={() => setIsFilterOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Filters">
          <SheetGrabber />
          <ModalHeader>
            <ModalTitle>Filters</ModalTitle>
            <CloseButton type="button" onClick={() => setIsFilterOpen(false)} aria-label="Close filters">
              ×
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            <FilterGroup>
              <FilterSectionTitle>Department</FilterSectionTitle>
              <FilterSelect
                value={modalDraft.department || ''}
                onChange={(e) => setModalDraft((d) => ({ ...d, department: e.target.value }))}
              >
                <option value="">All departments</option>
                {SHOP_DEPARTMENTS.map((dep) => (
                  <option key={dep} value={dep}>{dep}</option>
                ))}
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterSectionTitle>Category</FilterSectionTitle>
              <FilterSelect
                value={modalDraft.category || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setModalDraft((d) => ({
                    ...d,
                    category: val,
                    subcategory: '',
                    book_subcategory: '',
                    size: '',
                    pet_type: '',
                    book_type: '',
                    book_language: '',
                    book_audience: '',
                    length: '',
                  }));
                }}
              >
                <option value="">All categories</option>
                {filterCategories.filter((c) => c.value).map((c) => (
                  <option key={c.value} value={c.value}>{c.icon} {c.name}</option>
                ))}
              </FilterSelect>
            </FilterGroup>

            {getSubcategoriesForCategory(modalDraft.category).length > 0 && (
              <FilterGroup>
                <FilterSectionTitle>Subcategory</FilterSectionTitle>
                <FilterSelect
                  value={
                    isKitchenDiningSubcategory(modalDraft.subcategory || modalDraft.book_subcategory)
                      ? KITCHEN_DINING_ROOT
                      : (modalDraft.subcategory || modalDraft.book_subcategory || '')
                  }
                  onChange={(e) =>
                    setModalDraft((d) => ({
                      ...d,
                      subcategory: e.target.value,
                      book_subcategory: d.category === 'Books' ? e.target.value : d.book_subcategory,
                    }))
                  }
                >
                  <option value="">All</option>
                  {getSubcategoriesForCategory(modalDraft.category).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {modalDraft.category === 'Books' && (
              <>
                <FilterGroup>
                  <FilterSectionTitle>Book type</FilterSectionTitle>
                  <FilterSelect
                    value={modalDraft.book_type || ''}
                    onChange={(e) => setModalDraft((d) => ({ ...d, book_type: e.target.value }))}
                  >
                    <option value="">Any</option>
                    {BOOK_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterSectionTitle>Language</FilterSectionTitle>
                  <FilterSelect
                    value={modalDraft.book_language || ''}
                    onChange={(e) => setModalDraft((d) => ({ ...d, book_language: e.target.value }))}
                  >
                    <option value="">Any</option>
                    {BOOK_LANGUAGES.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterSectionTitle>Audience</FilterSectionTitle>
                  <FilterSelect
                    value={modalDraft.book_audience || ''}
                    onChange={(e) => setModalDraft((d) => ({ ...d, book_audience: e.target.value }))}
                  >
                    <option value="">Any</option>
                    {BOOK_AUDIENCES.map((audience) => (
                      <option key={audience} value={audience}>{audience}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
              </>
            )}

            {modalDraft.category === 'Pet Supplies' && (
              <FilterGroup>
                <FilterSectionTitle>Pet type</FilterSectionTitle>
                <FilterSelect
                  value={modalDraft.pet_type || ''}
                  onChange={(e) => setModalDraft((d) => ({ ...d, pet_type: e.target.value }))}
                >
                  <option value="">Any</option>
                  {PET_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            {isHomeKitchenListingCategory(modalDraft.category)
              && isKitchenDiningSubcategory(modalDraft.subcategory) && (
              <>
                <FilterGroup>
                  <FilterSectionTitle>Kitchen category</FilterSectionTitle>
                  <FilterSelect
                    value={kitchenFilter.group || ''}
                    onChange={(e) => setKitchenFilter({ group: e.target.value })}
                  >
                    <option value="">All</option>
                    {KITCHEN_DINING_GROUPS.map((group) => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                {kitchenFilter.group && (
                  <FilterGroup>
                    <FilterSectionTitle>Kitchen type</FilterSectionTitle>
                    <FilterSelect
                      value={kitchenFilter.type || ''}
                      onChange={(e) => setKitchenFilter({ type: e.target.value })}
                    >
                      <option value="">All</option>
                      {getKitchenDiningTypes(kitchenFilter.group).map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </FilterSelect>
                  </FilterGroup>
                )}
              </>
            )}

            <FilterGroup>
              <FilterSectionTitle>Size</FilterSectionTitle>
              <FilterSelect
                value={modalDraft.size || ''}
                onChange={(e) => setModalDraft((d) => ({ ...d, size: e.target.value }))}
              >
                <option value="">Any</option>
                {getBrowseSizeOptions(modalDraft.category).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </FilterSelect>
            </FilterGroup>

            {isClothingCategory(modalDraft.category) && (
              <FilterGroup>
                <FilterSectionTitle>Length</FilterSectionTitle>
                <FilterSelect
                  value={modalDraft.length || ''}
                  onChange={(e) => setModalDraft((d) => ({ ...d, length: e.target.value }))}
                >
                  <option value="">Any</option>
                  {Object.entries(FILTER_LENGTH_GROUPS).map(([group, options]) => (
                    <optgroup key={group} label={group}>
                      {options.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </optgroup>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}

            <FilterGroup>
              <FilterSectionTitle>Price range (EGP)</FilterSectionTitle>
              <FilterRow>
                <div>
                  <FilterLabel>Min</FilterLabel>
                  <FilterInput
                    type="number"
                    min="0"
                    placeholder="0"
                    value={modalDraft.minPrice || ''}
                    onChange={(e) => setModalDraft((d) => ({ ...d, minPrice: e.target.value }))}
                  />
                </div>
                <div>
                  <FilterLabel>Max</FilterLabel>
                  <FilterInput
                    type="number"
                    min="0"
                    placeholder="10000"
                    value={modalDraft.maxPrice || ''}
                    onChange={(e) => setModalDraft((d) => ({ ...d, maxPrice: e.target.value }))}
                  />
                </div>
              </FilterRow>
            </FilterGroup>

            <FilterGroup>
              <FilterSectionTitle>Brand</FilterSectionTitle>
              <BrandSelector
                mode="filter"
                filterValue={modalDraft.brand || ''}
                onFilterChange={(brandName) => setModalDraft((d) => ({ ...d, brand: brandName }))}
              />
            </FilterGroup>

            <FilterGroup>
              <FilterSectionTitle>Condition</FilterSectionTitle>
              <ChipGroup>
                {FILTER_CONDITIONS.map((c) => {
                  const selected = parseMultiFilterValue(modalDraft.condition).includes(c);
                  return (
                    <ConditionChip
                      key={c}
                      type="button"
                      $active={selected}
                      onClick={() =>
                        setModalDraft((d) => ({
                          ...d,
                          condition: toggleMultiFilterValue(d.condition, c),
                        }))
                      }
                    >
                      {c}
                    </ConditionChip>
                  );
                })}
              </ChipGroup>
            </FilterGroup>

            <FilterGroup>
              <FilterSectionTitle>Color</FilterSectionTitle>
              <FilterSelect
                value={modalDraft.color || ''}
                onChange={(e) => setModalDraft((d) => ({ ...d, color: e.target.value }))}
              >
                <option value="">Any</option>
                {Object.entries(FILTER_COLOR_GROUPS).map(([group, options]) => (
                  <optgroup key={group} label={group}>
                    {options.map((c) => (
                      <option key={`${group}-${c}`} value={c}>{c}</option>
                    ))}
                  </optgroup>
                ))}
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterSectionTitle>Material</FilterSectionTitle>
              <FilterSelect
                value={modalDraft.material || ''}
                onChange={(e) => setModalDraft((d) => ({ ...d, material: e.target.value }))}
              >
                <option value="">Any</option>
                {FILTER_MATERIALS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </FilterSelect>
            </FilterGroup>
          </ModalBody>
          <ModalFooter>
            <FooterButton
              type="button"
              className="secondary"
              onClick={() => {
                const cleared = {
                  ...EMPTY_BROWSE_FILTERS,
                  category: modalDraft.category || '',
                  sort: modalDraft.sort || '',
                  view: modalDraft.view || '',
                };
                setModalDraft(cleared);
                dispatch(clearReduxFilters());
              }}
            >
              Clear all
            </FooterButton>
            <FooterButton type="button" className="primary" onClick={applyModal}>
              Apply
            </FooterButton>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
      </PageInner>
    </Page>
  );
};

export default ItemsPage;
